import {
  AnalyticsPageEmployeeNamesFragment,
  AnalyticsPageLegalEntitiesFragment,
  AnalyticsPageLocationsFragment,
  TeamsTeam,
} from 'generated/resources/graphql'

import { GroupByType } from 'modules/performance_analytics/components/helpers'
import { useGroupByKeysLookupTransformer } from 'modules/performance_analytics/hooks/useGroupByKeysLookupTransformer'

import { OTHERS_BUCKET_NAME, TOP_BUCKETS_LIMIT } from './constants'
import { BucketScorePair, ChartDataItem, GroupByData } from './types'

export const useProcessChartData = (
  data: GroupByData,
  groupBy: GroupByType | null | undefined
): {
  chartData: ChartDataItem[]
  legalEntities: AnalyticsPageLegalEntitiesFragment[] | undefined
  teams: Pick<TeamsTeam, 'id' | 'name'>[] | undefined
  locations: AnalyticsPageLocationsFragment[] | undefined
  employees: AnalyticsPageEmployeeNamesFragment[] | undefined
} => {
  const { counts, values } = data

  const { buckets, loading, legalEntities, teams, locations, employees } =
    useGroupByKeysLookupTransformer(groupBy, values)
  if (loading) {
    return {
      chartData: [],
      legalEntities,
      teams,
      locations,
      employees,
    }
  }

  // Create bucket-score pairs
  const bucketScorePairs: BucketScorePair[] = values.map((value, index) => ({
    value,
    bucket: buckets[index] ?? value,
    score: counts[index] ?? 0,
  }))

  // Sort by score in descending order
  bucketScorePairs.sort((a, b) => b.score - a.score)

  // Take the top N buckets
  const topBuckets = bucketScorePairs.slice(0, TOP_BUCKETS_LIMIT)

  // Calculate the "Others" bucket
  const otherBuckets = bucketScorePairs.slice(TOP_BUCKETS_LIMIT)
  const othersScore = otherBuckets.reduce((sum, item) => sum + item.score, 0)

  // Create the final chart data
  const chartData: ChartDataItem[] = topBuckets.map((item) => ({
    bucket: item.bucket,
    score: item.score,
  }))

  // Add the "Others" bucket if there are buckets beyond the top limit
  if (otherBuckets.length > 0) {
    chartData.push({
      bucket: OTHERS_BUCKET_NAME,
      score: othersScore,
    })
  }

  return { chartData, legalEntities, teams, locations, employees }
}
